import React, { createContext, useCallback, useContext, useMemo, useRef, useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useI18n } from '../utils/i18n';
import './ui/ui.css';

// Globaler Toast-Stack (Frontend Phase 2 / F5): pushToast({ message, type,
// timeoutMs, actionLabel, onAction }) mit type 'info' | 'success' | 'error'.
// Seit Phase 2: Portal an document.body, aria-live-Region, Styles über die
// :root-Tokens (ui/ui.css) statt Inline-Hardcodes.
const ToastContext = createContext({
  pushToast: () => {},
  removeToast: () => {}
});

let nextToastId = 1;

// Anzeigedauer in ms. Nicht gesetzt (undefined/null/'') => Standard 5 s,
// 0 => bleibt stehen, Unsinn (NaN, negativ) => Standard.
function normalizeTimeout(value) {
  if (value === undefined || value === null || value === '') return 5000;
  const parsed = Number(value);
  if (!Number.isFinite(parsed) || parsed < 0) return 5000;
  return parsed;
}

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]); // [{id, message, actionLabel, onAction, type, timeoutMs, createdAt}]
  const timersRef = useRef(new Map());

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    const timers = timersRef.current;
    if (timers.has(id)) {
      clearTimeout(timers.get(id));
      timers.delete(id);
    }
  }, []);

  const pushToast = useCallback((toast) => {
    const id = nextToastId++;
    // #256: `timeoutMs: 0` heißt "bleibt stehen, bis der Nutzer ihn wegklickt".
    // Mit `toast?.timeoutMs || 5000` wurde daraus stillschweigend die Standarddauer
    // von 5 s — betraf ausgerechnet die drei Toasts, die stehenbleiben müssen
    // (Update verfügbar #16/B5, Sitzung abgelaufen #261, Verbindung gestört #229).
    const timeoutMs = normalizeTimeout(toast?.timeoutMs);
    const entry = {
      id,
      message: String(toast?.message || ''),
      actionLabel: toast?.actionLabel || null,
      onAction: typeof toast?.onAction === 'function' ? toast.onAction : null,
      type: toast?.type || 'info',
      timeoutMs
    };
    setToasts(prev => [entry, ...prev]);
    if (timeoutMs > 0) {
      const timer = setTimeout(() => removeToast(id), timeoutMs);
      timersRef.current.set(id, timer);
    }
    return id;
  }, [removeToast]);

  useEffect(() => () => {
    // cleanup all timers on unmount
    const timers = timersRef.current;
    for (const t of timers.values()) clearTimeout(t);
    timers.clear();
  }, []);

  const value = useMemo(() => ({ pushToast, removeToast }), [pushToast, removeToast]);

  // #329: bewusst NICHT `t` — in der Liste unten heisst die Laufvariable schon so,
  // ein gleichnamiges Hook-Ergebnis waere dort stillschweigend verschattet.
  const { t: uebersetze } = useI18n();

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div className="ui-toast-container" aria-live="polite">
          {toasts.map((t) => (
            <div key={t.id} className={`ui-toast ui-toast-${t.type}`} role="status">
              <div className="ui-toast-message">{t.message}</div>
              <div className="ui-toast-buttons">
                {t.actionLabel && t.onAction && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={async () => {
                      try { await t.onAction(); } finally { removeToast(t.id); }
                    }}
                  >{t.actionLabel}</button>
                )}
                <button
                  type="button"
                  className="ui-toast-close"
                  aria-label={uebersetze('common.close', 'Schließen')}
                  onClick={() => removeToast(t.id)}
                >×</button>
              </div>
            </div>
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
};

export const useToast = () => useContext(ToastContext);







